<template>
  <v-container fluid grid-list-lg class="showroom-page" id="ageAssuranceId">
    <v-dialog :width="videoWidth" v-model="video_dialog" content-class="showroom-video-dialog" lazy>
      <v-card class="showroom-video-card" elevation="0">
        <iframe
          :width="videoWidth"
          :height="videoWidth / 1.77"
          :src="selected_video_src"
          frameborder="0"
          allowfullscreen
        ></iframe>
      </v-card>
    </v-dialog>

    <v-layout row wrap class="showroom-shell">
      <v-flex xs12 class="mb-3">
        <v-card class="showroom-hero" elevation="0">
          <div class="showroom-hero-body">
            <div class="showroom-hero-copy">
              <div class="showroom-kicker">Privately SA Showroom</div>
              <h1 class="showroom-title">Privacy-preserving AI experiences</h1>
              <p class="showroom-description">
                On this page, you can find demos for different key technologies developed at Privately SA. These technologies are at the core of our online safety and wellbeing solutions.
              </p>
              <div class="showroom-badges">
                <span class="showroom-badge">Age Assurance</span>
                <span class="showroom-badge">Online Safety</span>
                <span class="showroom-badge">Federated Learning</span>
              </div>
            </div>
            <div class="hero-cert-column">
              <div class="hero-cert-stack">
                <a
                  class="hero-cert-link"
                  href="https://accscheme.com/registry/age-assurance/privately-sa/"
                  target="_blank"
                >
                  <v-img class="hero-logo" :src="require('@/assets/images/cert.png')" contain height="96" />
                </a>
                <a
                  class="hero-cert-link"
                  href="https://www.kjm-online.de/service/pressemitteilungen/meldung/kjm-bewertet-altersverifikationssystem-mit-biometrischer-alterskontrolle-positiv"
                  target="_blank"
                >
                  <v-img class="hero-logo" :src="require('@/assets/images/kjm.png')" contain height="96" />
                </a>
              </div>
            </div>
          </div>
        </v-card>
      </v-flex>

      <v-flex xs12 md4 lg3 class="showroom-sidebar">
        <v-card class="showroom-panel pa-3" elevation="0">
          <h3 class="panel-title">Filter demos</h3>
          <p class="panel-subtitle">Select one or more tags</p>

          <div class="panel-group-label">Use Case</div>
          <div class="chip-wrap">
            <v-chip
              v-for="(use_case, i) in use_cases"
              :key="i"
              label
              @click="selectTag(use_case)"
              class="tag-chip"
              :class="{ 'tag-chip-active': selectedFilters.includes(use_case) }"
            >
              # {{ use_case }}
            </v-chip>
          </div>

          <div class="panel-group-label mt-3">Modality</div>
          <div class="chip-wrap">
            <v-chip
              v-for="(modalityItem, i) in modality"
              :key="i"
              label
              @click="selectTag(modalityItem)"
              class="tag-chip"
              :class="{ 'tag-chip-active': selectedFilters.includes(modalityItem) }"
            >
              # {{ modalityItem }}
            </v-chip>
          </div>
        </v-card>

        <v-card class="showroom-panel pa-3 mt-3 hidden-sm-and-down section-nav" elevation="0">
          <div class="panel-title mb-2">Quick jump</div>
          <div
            :class="['nav-item', onAgeAssurance ? 'nav-item-active' : '']"
            @click="$vuetify.goTo('#ageAssuranceId', scrollOptions)"
          >
            <v-icon small>{{ onAgeAssurance ? "bookmark" : "bookmark_border" }}</v-icon>
            <span>Age Assurance</span>
          </div>
          <div
            :class="['nav-item', onOnlineSafety ? 'nav-item-active' : '']"
            @click="$vuetify.goTo('#onlineSafetyId', scrollOptions)"
          >
            <v-icon small>{{ onOnlineSafety ? "bookmark" : "bookmark_border" }}</v-icon>
            <span>Online Safety</span>
          </div>
          <div :class="['nav-item', onPet ? 'nav-item-active' : '']" @click="$vuetify.goTo('#petId', scrollOptions)">
            <v-icon small>{{ onPet ? "bookmark" : "bookmark_border" }}</v-icon>
            <span>PET</span>
          </div>
        </v-card>
      </v-flex>

      <v-flex xs12 md8 lg9 class="showroom-main">
        <section class="showroom-section">
          <div class="showroom-section-header">
            <div class="showroom-section-kicker">Category 01</div>
            <h2
              class="showroom-section-title"
              v-waypoint="{ active: true, callback: onAgeAssuranceWaypoint, options: intersectionOptions }"
            >
              Privacy Preserving Age Assurance
            </h2>
          </div>

          <div class="showroom-row-label">Videos</div>
          <v-layout row wrap>
            <v-flex xs12 md6 xl3 lg4 v-for="video in age_assurance_videos" :key="video.id" class="mb-3">
              <v-card
                class="showroom-video-tile"
                elevation="0"
                :height="isMd ? '290px' : '270px'"
                @click="openVideoDialog(video)"
              >
                <v-img
                  :src="video.img_src"
                  :lazy-src="require('@/assets/images/video-placeholder.png')"
                  width="100%"
                  height="150"
                />
                <v-card-title primary-title class="showroom-video-title">
                  {{ video.title }}
                </v-card-title>
              </v-card>
            </v-flex>
          </v-layout>

          <div class="showroom-row-label mt-3">Live Demos</div>
          <v-layout row wrap>
            <v-flex
              xs12
              md6
              xl3
              lg4
              v-for="demo in demos"
              :key="demo.id"
              class="mb-3"
              v-show="
                demo.category == 'age_assurance' &&
                (selectedFilters.length == 0 || selectedFilters.every((r) => demo.filter.includes(r)))
              "
            >
              <router-link :to="{ name: demo.routeName, params: demo.routeParams }" tag="div" class="demo-link">
                <div :class="demo.cardClass" class="demo-card" :style="{ height: isMd ? '160px !important' : '' }">
                  <v-card-title primary-title>
                    <div class="text-xs-left">
                      <ul class="title">
                        <li>{{ demo.title }}</li>
                      </ul>
                      <div class="demo-subtitle">{{ demo.subtitle }}</div>
                      <p v-if="isMaintenanceDemo(demo.cardClass)" class="demo-status">• Under Maintenance</p>
                    </div>
                  </v-card-title>
                </div>
              </router-link>
            </v-flex>
          </v-layout>
        </section>

        <section class="showroom-section" id="onlineSafetyId">
          <div class="showroom-section-header">
            <div class="showroom-section-kicker">Category 02</div>
            <h2
              class="showroom-section-title"
              v-waypoint="{ active: true, callback: onOnlineSafetyWaypoint, options: intersectionOptions }"
            >
              Privacy Preserving Online Safety
            </h2>
          </div>

          <div class="showroom-row-label">Videos</div>
          <v-layout row wrap>
            <v-flex xs12 md6 xl3 lg4 v-for="video in online_safety_videos" :key="video.id" class="mb-3">
              <v-card class="showroom-video-tile" elevation="0" height="250px" @click="openVideoDialog(video)">
                <v-img
                  :src="video.img_src"
                  :lazy-src="require('@/assets/images/video-placeholder.png')"
                  width="100%"
                  height="150"
                />
                <v-card-title primary-title class="showroom-video-title">
                  {{ video.title }}
                </v-card-title>
              </v-card>
            </v-flex>
          </v-layout>

          <div class="showroom-row-label mt-3">Live Demos</div>
          <v-layout row wrap>
            <v-flex
              xs12
              md6
              xl3
              lg4
              v-for="demo in demos"
              :key="demo.id"
              class="mb-3"
              v-show="
                demo.category == 'online_safety' &&
                (selectedFilters.length == 0 || selectedFilters.every((r) => demo.filter.includes(r)))
              "
            >
              <router-link :to="{ name: demo.routeName, params: demo.routeParams }" tag="div" class="demo-link">
                <div :class="demo.cardClass" class="demo-card">
                  <v-card-title primary-title>
                    <div class="text-xs-left">
                      <ul class="title">
                        <li>{{ demo.title }}</li>
                      </ul>
                      <div class="demo-subtitle">{{ demo.subtitle }}</div>
                    </div>
                  </v-card-title>
                </div>
              </router-link>
            </v-flex>
          </v-layout>
        </section>

        <section class="showroom-section" id="petId">
          <div class="showroom-section-header">
            <div class="showroom-section-kicker">Category 03</div>
            <h2 class="showroom-section-title">Privacy Enhancing Technologies (PET)</h2>
          </div>

          <div class="showroom-row-label">Videos</div>
          <v-layout row wrap>
            <v-flex xs12 md6 xl3 lg4 v-for="video in federated_learning_videos" :key="video.id" class="mb-3">
              <v-card class="showroom-video-tile" elevation="0" height="270px" @click="openVideoDialog(video)">
                <v-img
                  :src="video.img_src"
                  width="100%"
                  height="150"
                  :lazy-src="require('@/assets/images/video-placeholder.png')"
                />
                <v-card-title primary-title class="showroom-video-title">
                  {{ video.title }}
                </v-card-title>
              </v-card>
            </v-flex>
          </v-layout>

          <div v-waypoint="{ active: true, callback: onPetWaypoint, options: intersectionOptions }"></div>
        </section>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  created() {
    window.addEventListener("scroll", this.handleScroll);
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  data() {
    return {
      scrollOptions: {
        duration: 300,
        offset: 0,
        easing: "easeInOutCubic",
      },
      intersectionOptions: {
        root: null,
        rootMargin: "0px 0px 0px 0px",
        threshold: [0, 1],
      },
      windowTop: 0,
      onAgeAssurance: true,
      onOnlineSafety: false,
      onPet: false,
      video_dialog: false,
      selected_video_src: "",
      age_assurance_videos: [
        {
          title: "Zero Data Age Assurance: Privacy preserving On-Browser implementation",
          url: "https://youtube.com/embed/XLQeafp10bo",
          img_src: require("@/assets/images/zero-data-min.jpg"),
          id: 11,
        },
        {
          title: "Age Appropriate Advertizing in Web 3.0",
          url: "https://youtube.com/embed/615mPeKxR_k",
          img_src: require("@/assets/images/age-appropriate-min.jpg"),
          id: 22,
        },
        {
          title: "On- Device Age Verification (NFC ID Check)",
          url: "https://youtube.com/embed/kj5ai_uWGok",
          img_src: require("@/assets/images/on-device-min.jpeg"),
          id: 33,
        },
      ],
      online_safety_videos: [
        {
          title: "Preventing Child Sexual Abuse",
          url: "https://youtube.com/embed/GFnysaO60Hg",
          img_src: require("@/assets/images/child-abuse-min.jpeg"),
          id: 44,
        },
        {
          title: "Hate/Toxicity Reduction through nudges",
          url: "https://youtube.com//embed/_B7pJksXi_w",
          img_src: require("@/assets/images/hate-toxicity-min.jpeg"),
          id: 55,
        },
      ],
      federated_learning_videos: [
        {
          title: "Federated Learning - Training Models from Decentralized Data",
          url: "https://youtube.com/embed/Zww6hceXgws",
          img_src: require("@/assets/images/federated-learning-min.jpeg"),
          id: 66,
        },
        {
          title: "Federated Learning On-Device",
          url: "https://youtube.com/embed/_3u8Hotxrhs",
          img_src: require("@/assets/images/fl-ondevice.jpg"),
          id: 77,
        },
      ],
      show: true,
      selectedTag: "",
      selectedFilters: [],
      drawer: null,
      use_cases: ["age-estimation", "geniuneness", "online-safety", "emotions"],
      modality: ["image", "voice", "text"],
    };
  },
  watch: {
    video_dialog: function (newValue) {
      if (!newValue) {
        this.selected_video_src = "";
      }
    },
  },
  methods: {
    onAgeAssuranceWaypoint({ going, direction }) {
      if (going === this.$waypointMap.GOING_IN) {
        this.onAgeAssurance = true;
      }
      if (going === this.$waypointMap.GOING_OUT || direction === this.$waypointMap.DIRECTION_TOP) {
        this.onAgeAssurance = false;
      }
    },

    onOnlineSafetyWaypoint({ going }) {
      if (going === this.$waypointMap.GOING_OUT) {
        this.onOnlineSafety = false;
      }
      if (going === this.$waypointMap.GOING_IN) {
        this.onOnlineSafety = true;
      }
    },

    onPetWaypoint({ going }) {
      if (going === this.$waypointMap.GOING_IN) {
        this.onPet = true;
      }
      if (going === this.$waypointMap.GOING_OUT) {
        this.onPet = false;
      }
    },
    openVideoDialog(item) {
      this.video_dialog = true;
      this.selected_video_src = item.url;
    },
    isMaintenanceDemo(cardClass) {
      return (
        cardClass === "textAssure" ||
        cardClass === "ageAssure" ||
        cardClass === "faceAssure" ||
        cardClass === "voiceAssure"
      );
    },
    selectTag(value) {
      const index = this.selectedFilters.indexOf(value);
      if (index > -1) {
        this.selectedFilters.splice(index, 1);
      } else {
        this.selectedFilters.push(value);
      }
    },
  },
  computed: {
    ...mapGetters("common", {
      pageElements: "pageElements",
    }),
    videoWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 350;
        case "sm":
          return 500;
        case "md":
          return 763;
        case "lg":
          return 763;
        case "xl":
          return 763;
        default:
          return 763;
      }
    },
    isMobile() {
      return this.$vuetify.breakpoint.name === "xs" || this.$vuetify.breakpoint.name === "sm";
    },
    isMd() {
      return this.$vuetify.breakpoint.name === "md";
    },
    isVertical() {
      return (
        this.$vuetify.breakpoint.name == "lg" ||
        this.$vuetify.breakpoint.name == "xl" ||
        this.$vuetify.breakpoint.name == "md"
      );
    },
    demos() {
      let items = [
        {
          title: "AgeAssure - Multimodal Demo",
          subtitle: "Facial and Voice Analysis for Age determination (no anti-spoof)",
          routeName: "",
          routeParams: {},
          dark: true,
          filter: ["age-estimation", "image", "voice"],
          category: "age_assurance",
          cardClass: "ageAssure",
          id: 111,
        },
        {
          title: "FaceAssure",
          subtitle: "Coarse-grained Age Estimation using Facial Analysis",
          routeName: "",
          routeParams: {},
          dark: true,
          filter: ["age-estimation", "geniuneness", "image"],
          category: "age_assurance",
          cardClass: "faceAssure",
          id: 222,
        },
        {
          title: "VoiceAssure",
          subtitle: "Age Estimation using Voice Analysis - with Antispoofing",
          routeName: "",
          routeParams: {},
          dark: true,
          filter: ["age-estimation", "geniuneness", "voice"],
          category: "age_assurance",
          cardClass: "voiceAssure",
          id: 333,
        },
        {
          title: "TextAssure",
          subtitle: "Estimate a user's age from input text",
          routeName: "",
          routeParams: {},
          dark: true,
          explanation: "This demo includes classifiers for estimating a user's age",
          filter: ["age-estimation", "text"],
          category: "age_assurance",
          cardClass: "textAssure",
          id: 444,
        },
        {
          title: "Voice Similarity",
          subtitle: "Reports if two audio samples come from the same speaker",
          routeName: "voice_print",
          routeParams: {},
          dark: true,
          filter: ["geniuneness", "text"],
          category: "age_assurance",
          cardClass: "voiceSimilarity",
          id: 555,
        },
        {
          title: "On-Browser Voice Captcha",
          subtitle: "Check if your form is filled by a geniune person",
          routeName: "voice_captcha_demo",
          routeParams: {},
          dark: true,
          filter: ["geniuneness", "voice"],
          category: "age_assurance",
          cardClass: "voiceCaptcha",
          id: 666,
        },
        {
          title: "Restricted Retail",
          subtitle: "Age Verification for Online Retail",
          routeName: "restrictedretail",
          routeParams: {},
          dark: true,
          filter: ["age-estimation", "geniuneness", "image"],
          category: "age_assurance",
          cardClass: "restrictedRetail",
          id: 777,
        },
        {
          title: "CSAM Images Detection",
          subtitle: "Detecting nudity and age in images",
          routeName: "csam_demo",
          routeParams: {},
          dark: true,
          explanation: "",
          filter: ["age-estimation", "online-safety", "image"],
          category: "online_safety",
          cardClass: "csamDetection",
          id: 888,
        },
        {
          title: "Hate & Toxicity",
          subtitle: "Evaluating online speech in multiple languages",
          routeName: "hate_toxicity",
          routeParams: {},
          dark: true,
          explanation: "This demo includes classifiers for detecting hate speech and toxicity.",
          filter: ["online-safety", "emotions", "text"],
          category: "online_safety",
          cardClass: "hateToxicity",
          id: 999,
        },

        {
          title: "Emotion Analysis in Text",
          subtitle: "Angry? Joyful? Neutral? or What?",
          routeName: "emotion",
          routeParams: {},
          dark: true,
          explanation: "",
          filter: ["emotions", "text"],
          category: "online_safety",
          cardClass: "emotionAnalysis",
          id: 1111,
        },

        {
          title: "Nudity Detection",
          subtitle: "Detection of nudity in images",
          routeName: "nudity_demo",
          routeParams: {},
          dark: true,
          explanation: "",
          filter: ["online-safety", "image"],
          category: "online_safety",
          cardClass: "nudityDetection",
          id: 2222,
        },
      ];
      for (const item of items) {
        try {
          item.color = this.pageElements[item.routeName].color;
          item.img = this.pageElements[item.routeName].img;
        } catch (e) {
          // Keep default demo color/image when route config is unavailable.
        }
      }

      return items;
    },
  },
};
</script>

<style scoped>
.showroom-video-card iframe {
  display: block;
}
</style>
